import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	type PropsWithChildren,
	Fragment,
} from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment, useMutation } from 'react-relay';
import { v4 as uuid } from 'uuid';

import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { UNSAFE_noExposureExp, expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import type { SessionId } from '@atlassian/jira-issue-analytics/src/common/types.tsx';
import { useInlineEditFieldInjections } from '@atlassian/jira-issue-field-injections/src/controllers/inline-edit-injections-context/index.tsx';
import { useFieldInlineEditActions } from '@atlassian/jira-issue-field-inline-edit-actions/src/controllers/index.tsx';
import type { OnSubmitCallbacks } from '@atlassian/jira-issue-field-inline-edit-actions/src/controllers/types.tsx';
import type { ValidationFieldProps } from '@atlassian/jira-issue-field-inline-edit-lite/src/ui/field-inline-edit-lite/types.tsx';
import { FieldInlineEditLiteWithEntryPoint } from '@atlassian/jira-issue-field-inline-edit-lite/src/ui/index.tsx';
// importing this from the old labels field to avoid large bundle size increases, will need to move this over when deleting old labels field.
import LabelsEditViewEntryPoint from '@atlassian/jira-issue-field-labels-editview-full/src/entrypoint.tsx';
import type {
	Option,
	LabelsEditViewProps,
} from '@atlassian/jira-issue-field-labels-editview-full/src/ui/labels/types.tsx';
import { LabelsReadView } from '@atlassian/jira-issue-field-labels-readview-full/src/ui/labels/index.tsx';
import { useLabelsCache } from '@atlassian/jira-issue-field-labels/src/services/labels-cache-service/index.tsx';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { labels_issueFieldLabelsInlineEditViewFull_LabelsInlineEditView$key as LabelsInlineEditViewFragment } from '@atlassian/jira-relay/src/__generated__/labels_issueFieldLabelsInlineEditViewFull_LabelsInlineEditView.graphql';
import type {
	JiraLabelsFieldOperationInput,
	labels_issueFieldLabelsInlineEditViewFull_LabelsInlineEditViewMutation as LabelsInlineEditViewMutation,
} from '@atlassian/jira-relay/src/__generated__/labels_issueFieldLabelsInlineEditViewFull_LabelsInlineEditViewMutation.graphql';
import type {
	labels_issueFieldLabelsInlineEditViewFull_LabelsInlineEditViewWithIsEditable_fragmentRef$key as LabelsInlineEditViewWithIsEditableFragment,
	labels_issueFieldLabelsInlineEditViewFull_LabelsInlineEditViewWithIsEditable_fragmentRef$data as LabelsInlineEditViewData,
} from '@atlassian/jira-relay/src/__generated__/labels_issueFieldLabelsInlineEditViewFull_LabelsInlineEditViewWithIsEditable_fragmentRef.graphql';
import { GREY_META } from '@atlassian/jira-option-color-picker/src/color-picker/constants.tsx';
import {
	useCallbackWithCondition,
	useMemoWithCondition,
} from '@atlassian/jira-feature-flagging-utils/src/utils/hooks/index.tsx';
import type { Operation } from '../../useMultiSelectFieldDirtyValue.tsx';
import type {
	LabelsInlineEditViewProps,
	LabelsInlineEditViewWithIsEditableProps,
	ColoredLabel,
} from './types.tsx';
import {
	getProjectScopeLabelCheckboxValueFromLocalStorage,
	setProjectScopeLabelCheckboxValueFromLocalStorage,
	useLabelsValidator,
} from './utils.tsx';

const MaybeUFOSegment = componentWithFG(
	'nin_blindspot_fixes_fy26',
	({ children }: PropsWithChildren<{}>) => (
		<UFOSegment name="issue-field-labels-read-view" mode="list">
			{children}
		</UFOSegment>
	),
	Fragment,
);

const FIELD_KEY = 'labels';
const FIELD_TYPE = 'labels';
export const CODE_LOCATION = 'issue-field.save-value';
export const ERROR_MESSAGE = `Failed to update field ${FIELD_KEY} of type ${FIELD_TYPE}`;

const transformAggToAtlaskitOption = (
	// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
	connection: LabelsInlineEditViewData['selectedLabelsConnection'] | null | void,
): Option[] => {
	const labelValues =
		connection?.edges
			?.map((edge) => edge?.node ?? null)
			.filter(Boolean)
			.filter((label) => label && label.name != null) || [];
	const labelOptions: Option[] = labelValues.map((label) => ({
		label: label.name || '',
		value: label.name || '',
		...(label.color ? { color: label.color.colorKey } : {}),
	}));
	return labelOptions;
};

const transformAtlaskitOptionToAgg = (
	value: Option[],
): LabelsInlineEditViewData['selectedLabelsConnection'] => ({
	totalCount: value.length,
	edges: value.map((label) => ({
		node: {
			name: label.value,
			...(label.color ? { color: { colorKey: label.color } } : { color: null }),
		},
	})),
});

const transformLabelsOptionsToLabelNames = (value: Option[]): string[] =>
	value?.map((labelOption) => labelOption.value) ?? [];

const transformLabelsOptionsToColoredLabels = (labels: Option[]): ColoredLabel[] => {
	// @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'JiraOptionColorInput | null | undefined'.
	return (
		labels?.map(({ value, color, __isNew__ }) => {
			if (color) {
				return { name: value, color };
			}
			return { name: value, ...(__isNew__ ? { color: GREY_META.name } : {}) };
		}) ?? []
	);
};

const transformOptionsOperationsIntoAggOperationInput = (
	operations: Operation<Option>[],
): JiraLabelsFieldOperationInput[] =>
	operations.map(({ operation, value }) => {
		if (
			expVal(
				'adding-color-to-labels-field-experiment-milestone2',
				'isColorfulNewLabelsEnabled',
				false,
			)
		) {
			const coloredLabels = transformLabelsOptionsToColoredLabels(value);
			return {
				operation,
				labels: coloredLabels?.map((label) => label.name) || [],
				labelProperties: coloredLabels,
			};
		}
		return {
			operation,
			labels: transformLabelsOptionsToLabelNames(value),
		};
	});

const isEqualLabels = (a: Option[], b: Option[]): boolean =>
	a.length === b.length &&
	a.every((option, index) => option.label === b[index].label && option.value === b[index].value);

export const LabelsInlineEditViewWithIsEditable = ({
	attributes,
	spacing = 'compact',
	baseSearchUrl,
	editViewPopup,
	editViewPopupAlignBlock,
	editViewPopupWidth,
	editViewPopupTarget,
	isTruncated,
	isEditable,
	menuPosition,
	menuPortalTarget,
	menuShouldScrollIntoView,
	onCancel,
	onEdit,
	onSubmit,
	onSubmitSucceeded: onSubmitSucceededProp,
	onSubmitFailed: onSubmitFailedProp,
	fragmentRef,
	readViewFitContainerHeight,
	shouldShowProjectScopedCheckbox,
	shouldPrefetchSuggestions = false,
	isHoverPopoverEnabled,
}: LabelsInlineEditViewWithIsEditableProps): React.JSX.Element => {
	const sessionId = useRef<SessionId>(uuid());
	const { overriding } = useInlineEditFieldInjections();
	const [currentProjectOnly, setCurrentProjectOnly] = useState(
		getProjectScopeLabelCheckboxValueFromLocalStorage,
	);

	useEffect(() => {
		fg('labels_restricted_to_project_experiment') &&
			setProjectScopeLabelCheckboxValueFromLocalStorage(currentProjectOnly);
	}, [currentProjectOnly]);

	const data = useFragment<LabelsInlineEditViewWithIsEditableFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment labels_issueFieldLabelsInlineEditViewFull_LabelsInlineEditViewWithIsEditable_fragmentRef on JiraLabelsField {
				...labels_issueFieldLabelsReadviewFull_LabelsReadView
				# eslint-disable-next-line @atlassian/relay/must-colocate-fragment-spreads
				...labels_issueFieldLabelsEditviewFull_LabelsEditView_nodeIdFragment
				id
				name
				fieldId
				type
				selectedLabelsConnection(first: 1000) @required(action: THROW) {
					# eslint-disable-next-line @atlassian/relay/unused-fields
					totalCount
					edges {
						node {
							name
							color {
								colorKey
							}
						}
					}
				}
			}
		`,
		fragmentRef,
	);

	const { id: uniqueFieldId, name: fieldNameNoOverrides, fieldId, type } = data;
	const isFieldEditable = overriding.overrideIsEditable(isEditable);
	const fieldName = overriding.overrideLabel(fieldNameNoOverrides);
	const initialValue = useMemo(
		() => transformAggToAtlaskitOption(data?.selectedLabelsConnection),
		[data?.selectedLabelsConnection],
	);

	const [newValue, setNewValue] = useState<Option[]>(initialValue);

	const [cachedLabels, { addToCache }] = useLabelsCache(fieldId);

	// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
	const [commit] = useMutation<LabelsInlineEditViewMutation>(graphql`
		mutation labels_issueFieldLabelsInlineEditViewFull_LabelsInlineEditViewMutation(
			$input: JiraUpdateLabelsFieldInput!
		) @raw_response_type {
			jira {
				updateLabelsField(input: $input) {
					success
					errors {
						message
					}
					field {
						...labels_issueFieldLabelsInlineEditViewFull_LabelsInlineEditViewWithIsEditable_fragmentRef
					}
				}
			}
		}
	`);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const validator = useLabelsValidator();

	const handleSubmit = useCallback(
		(value: Option[], { onSuccess, onFail }: OnSubmitCallbacks) => {
			const updatedValue = transformLabelsOptionsToLabelNames(value);
			const updatedColoredValue = transformLabelsOptionsToColoredLabels(value);
			addToCache(
				expVal(
					'adding-color-to-labels-field-experiment-milestone2',
					'isColorfulNewLabelsEnabled',
					false,
				)
					? updatedColoredValue
					: updatedValue,
			);
			onSubmit?.(
				expVal(
					'adding-color-to-labels-field-experiment-milestone2',
					'isColorfulNewLabelsEnabled',
					false,
				)
					? updatedColoredValue
					: updatedValue,
			);
			commit({
				variables: {
					input: {
						id: uniqueFieldId,
						operations: transformOptionsOperationsIntoAggOperationInput([
							{
								operation: 'SET',
								value,
							},
						]),
					},
				},
				optimisticResponse: {
					jira: {
						updateLabelsField: {
							success: true,
							errors: null,
							field: {
								id: uniqueFieldId,
								name: fieldName,
								type,
								fieldId,
								// @ts-expect-error - ts2322 - Property 'id' is missing in type '{ readonly colorKey: string | null | undefined; }' but required in type '{ readonly colorKey: string | null | undefined; readonly id: string | null | undefined; }'.
								selectedLabelsConnection: transformAtlaskitOptionToAgg(value),
							},
						},
					},
				},
				onCompleted(mutationData) {
					if (mutationData?.jira?.updateLabelsField?.success) {
						onSuccess();
					} else {
						const errorMessage =
							mutationData?.jira?.updateLabelsField?.errors?.[0]?.message ?? undefined;
						onFail(undefined, errorMessage);
						log.safeErrorWithoutCustomerData(
							CODE_LOCATION,
							`Failed to update field ${fieldId} of type ${FIELD_TYPE}`,
						);
					}
				},
				onError(error: Error) {
					onFail(error, error.message);
					log.safeErrorWithoutCustomerData(
						CODE_LOCATION,
						`Failed to update field ${fieldId} of type ${FIELD_TYPE}`,
					);
				},
			});
		},
		// PLEASE CONFIRM THESE DEPENDENCIES ARE CORRECT
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[addToCache, onSubmit, commit, uniqueFieldId, fieldName, type, fieldId, createAnalyticsEvent],
	);

	const handleSubmitSucceeded = useCallbackWithCondition(
		(value: Option[]) => {
			// onSubmitSucceededProp expects a list of strings so we must map our updated value accordingly.
			onSubmitSucceededProp?.(
				expVal(
					'adding-color-to-labels-field-experiment-milestone2',
					'isColorfulNewLabelsEnabled',
					false,
				)
					? transformLabelsOptionsToColoredLabels(value)
					: transformLabelsOptionsToLabelNames(value),
			);
		},
		[onSubmitSucceededProp],
		() => fg('jfp-magma-stability-issue-view-2'),
	);

	const [config] = UNSAFE_noExposureExp('labels_restricted_to_project');

	const memoizedAttributes = useMemoWithCondition(
		() => ({
			...attributes,
			...(fg('labels_restricted_to_project_experiment')
				? {
						hasProjectScopedLabels: config.get<boolean>('showProjectFilter', false),
						currentProjectOnly,
					}
				: {}),
		}),
		[attributes, config, currentProjectOnly],
		() => fg('jfp-magma-stability-issue-view-2'),
	);

	const {
		handleCancel,
		handleEdit,
		handleConfirm,
		handleChange,
		handleCopy,
		handleCut,
		handlePaste,
		hasServerValidationError,
		invalidMessage,
		isEditing,
	} = useFieldInlineEditActions({
		attributes: memoizedAttributes,
		fieldId,
		fieldName,
		fieldType: type,
		initialValue,
		isValueEqual: isEqualLabels,
		onCancel,
		onEdit,
		onSubmit: handleSubmit,
		onSubmitSucceeded: handleSubmitSucceeded,
		onSubmitFailed: onSubmitFailedProp,
		onUpdateValue: setNewValue,
		updatedValue: newValue,
		validator,
		clipboardComponentType: 'labels',
	});

	const renderReadView = useCallback(
		() => (
			<MaybeUFOSegment>
				<LabelsReadView
					baseSearchUrl={baseSearchUrl}
					fragmentRef={data}
					isTruncated={isTruncated}
					isHoverPopoverEnabled={isHoverPopoverEnabled}
				/>
			</MaybeUFOSegment>
		),
		[baseSearchUrl, data, isTruncated, isHoverPopoverEnabled],
	);

	const getEditViewProps = (fieldProps: ValidationFieldProps): LabelsEditViewProps => ({
		...fieldProps,
		autoFocus: true,
		ariaLabel: fieldName,
		spacing,
		menuPosition,
		menuPortalTarget,
		menuShouldScrollIntoView,
		onChange: handleChange,
		value: newValue,
		cachedOptions: cachedLabels,
		nodeIdFragment: data,
		isInvalid: !!invalidMessage,
		sessionId: sessionId.current,
		testId: 'issue-field-labels-inline-edit-view-full.ui.labels.edit-view',
		shouldShowProjectScopedCheckbox,
		...(fg('labels_restricted_to_project_experiment')
			? { currentProjectOnly, setCurrentProjectOnly }
			: {}),
	});

	return (
		<FieldInlineEditLiteWithEntryPoint
			editViewPopup={editViewPopup}
			editViewPopupAlignBlock={editViewPopupAlignBlock}
			editViewPopupTarget={editViewPopupTarget}
			editViewPopupWidth={editViewPopupWidth}
			editViewEntryPoint={LabelsEditViewEntryPoint}
			editViewEntryPointParams={shouldPrefetchSuggestions ? { id: uniqueFieldId } : {}}
			getEditViewProps={getEditViewProps}
			fieldName={fieldName}
			invalidMessage={invalidMessage}
			isEditable={isFieldEditable}
			isEditing={isEditing}
			hasReadViewTruncateDisabled={isHoverPopoverEnabled}
			readViewFitContainerHeight={readViewFitContainerHeight}
			readView={renderReadView}
			onCancel={handleCancel}
			onConfirm={handleConfirm}
			onEdit={handleEdit}
			onCopy={handleCopy}
			onCut={handleCut}
			onPaste={handlePaste}
			hasUnsubmittedChanges={hasServerValidationError}
		/>
	);
};

export const LabelsInlineEditView = ({
	fragmentRef,
	...props
}: LabelsInlineEditViewProps): React.JSX.Element => {
	const data = useFragment<LabelsInlineEditViewFragment>(
		/* eslint-disable @atlassian/relay/graphql-naming */
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment labels_issueFieldLabelsInlineEditViewFull_LabelsInlineEditView on JiraLabelsField {
				...labels_issueFieldLabelsInlineEditViewFull_LabelsInlineEditViewWithIsEditable_fragmentRef
				fieldConfig {
					isEditable
				}
			}
		`,
		fragmentRef,
		/* eslint-enable @atlassian/relay/graphql-naming */
	);

	return (
		<LabelsInlineEditViewWithIsEditable
			{...props}
			fragmentRef={data}
			isEditable={data.fieldConfig?.isEditable ?? false}
		/>
	);
};
